import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { FileText, Table2, Mail, Download, Loader2 } from 'lucide-react';
import { useMembresQuery, getAttestationStatus } from '@/hooks/queries/useMembresQuery';
import { useEtablissement } from '@/contexts/EtablissementContext';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

interface ExportConformiteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type ExportOption = 'pdf' | 'csv' | 'email';

/**
 * Modal d'export du rapport de conformité
 * 3 options : PDF officiel, Excel/CSV, Email
 */
export const ExportConformiteDialog = ({ open, onOpenChange }: ExportConformiteDialogProps) => {
  const [selected, setSelected] = useState<ExportOption>('csv');
  const [exporting, setExporting] = useState(false);
  const { data: membres } = useMembresQuery();
  const { etablissement } = useEtablissement();
  const { toast } = useToast();

  const options: { value: ExportOption; icon: typeof FileText; title: string; description: string; disabled?: boolean }[] = [
    {
      value: 'pdf',
      icon: FileText,
      title: 'PDF officiel',
      description: 'Document horodaté avec mention légale GetProof — pour un contrôle ARS (bientôt disponible)',
      disabled: true,
    },
    {
      value: 'csv',
      icon: Table2,
      title: 'Excel / CSV',
      description: 'Tableau complet pour usage interne ou archivage',
    },
    {
      value: 'email',
      icon: Mail,
      title: 'Envoyer par email',
      description: 'Recevoir le rapport par email ou le transmettre à un tiers (bientôt disponible)',
      disabled: true,
    },
  ];

  const handleExport = async () => {
    if (selected === 'csv') {
      setExporting(true);
      try {
        generateCSV();
        toast({
          title: 'Export réussi',
          description: 'Le fichier CSV a été téléchargé.',
        });
        onOpenChange(false);
      } catch (err: any) {
        toast({
          title: 'Erreur',
          description: err.message || "Impossible de générer l'export",
          variant: 'destructive',
        });
      } finally {
        setExporting(false);
      }
    }
  };

  /**
   * Génère et télécharge un fichier CSV avec les données de conformité
   */
  const generateCSV = () => {
    if (!membres || membres.length === 0) {
      throw new Error('Aucun membre à exporter');
    }

    const now = new Date();
    const header = [
      'Nom',
      'Prénom',
      'Email',
      'Poste',
      'Type contrat',
      'Date embauche',
      'Statut attestation',
      'Date réception',
      'Date délivrance',
      'Date expiration',
      'Date renouvellement',
      'Fichier attestation',
    ].join(';');

    const rows = membres.map(membre => {
      const status = getAttestationStatus(membre);
      const statusLabels: Record<string, string> = {
        conforme: 'Conforme',
        en_attente: 'En attente',
        expiree: 'Expirée',
        a_regulariser: 'À régulariser',
      };

      return [
        membre.nom,
        membre.prenom,
        membre.email,
        membre.poste || '',
        membre.type_contrat,
        membre.date_embauche || '',
        statusLabels[status] || status,
        membre.attestation?.received_at || '',
        membre.attestation?.date_delivrance || '',
        membre.attestation?.date_expiration || '',
        membre.attestation?.date_renouvellement || '',
        membre.attestation?.nom_fichier || '',
      ].map(val => `"${String(val).replace(/"/g, '""')}"`).join(';');
    });

    // Ajouter un en-tête avec les métadonnées
    const metadata = [
      `# Rapport de conformité — ${etablissement?.nom || 'Établissement'}`,
      `# Généré le ${format(now, "d MMMM yyyy 'à' HH:mm", { locale: fr })} (UTC+${-now.getTimezoneOffset() / 60})`,
      `# Membres actifs : ${membres.length}`,
      '',
    ].join('\n');

    const csvContent = metadata + header + '\n' + rows.join('\n');

    // Télécharger le fichier
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `conformite_${etablissement?.nom?.replace(/\s+/g, '_') || 'export'}_${format(now, 'yyyy-MM-dd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="app-content-centered-dialog max-w-[620px] rounded-[24px] border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface))] p-0 shadow-[0_24px_80px_rgba(15,23,42,0.18)]">
        <DialogHeader className="border-b border-[hsl(var(--app-border-subtle))] px-6 pb-5 pt-6">
          <DialogTitle className="text-[20px] font-semibold tracking-[-0.02em] text-[hsl(var(--app-text-strong))]">
            Exporter le rapport
          </DialogTitle>
          <DialogDescription className="mt-1 text-[14px] leading-6 text-[hsl(var(--app-text-muted))]">
            Choisissez le format d'export pour votre rapport de conformité.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 px-6 py-5">
          {options.map(opt => (
            <button
              key={opt.value}
              onClick={() => !opt.disabled && setSelected(opt.value)}
              disabled={opt.disabled}
              className={`flex w-full items-start gap-4 rounded-[18px] border p-4 text-left transition-colors ${
                opt.disabled
                  ? 'cursor-not-allowed border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-muted))] opacity-60'
                  : selected === opt.value
                    ? 'border-primary/30 bg-primary/5'
                    : 'border-[hsl(var(--app-border-subtle))] hover:bg-[hsl(var(--app-surface-muted))]'
              }`}
            >
              <div className={`rounded-[14px] p-2.5 ${
                selected === opt.value && !opt.disabled
                  ? 'border border-primary/20 bg-primary/10'
                  : 'border border-[hsl(var(--app-border-subtle))] bg-white'
              }`}>
                <opt.icon className={`w-5 h-5 ${
                  selected === opt.value && !opt.disabled ? 'text-primary' : 'text-[hsl(var(--app-text-muted))]'
                }`} />
              </div>
              <div>
                <p className="text-[14px] font-medium text-[hsl(var(--app-text-strong))]">{opt.title}</p>
                <p className="mt-1 text-[12px] leading-5 text-[hsl(var(--app-text-muted))]">{opt.description}</p>
              </div>
            </button>
          ))}
        </div>

        <DialogFooter className="border-t border-[hsl(var(--app-border-subtle))] px-6 py-4">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="rounded-[14px] border-[hsl(var(--app-border-subtle))]"
          >
            Annuler
          </Button>
          <Button
            onClick={handleExport}
            disabled={exporting || options.find(o => o.value === selected)?.disabled}
            className="gap-2 rounded-[14px]"
          >
            {exporting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
            {exporting ? 'Export...' : 'Télécharger'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
